import { colors } from '@airship-platform/jam-design-tokens';
import { SvgIconProps } from '@mui/material';
import React from 'react';

type RebateIconTheme = 'AIRSHIP_BLUE' | 'AIRSHIP_RED' | 'AIRSHIP_GOLD';

interface DefaultRebateIconProps extends SvgIconProps {
  theme?: RebateIconTheme;
}

interface RebateIconColors {
  primaryDark: string;
  primaryLight: string;
  secondaryDark: string;
  secondaryLight: string;
  accentDark: string;
  accentLight: string;
}

const COLORS_BY_THEME: Record<RebateIconTheme, RebateIconColors> = {
  AIRSHIP_BLUE: {
    primaryDark: colors.blue[900],
    primaryLight: colors.blue[800],
    secondaryDark: colors.blue[600],
    secondaryLight: colors.blue[500],
    accentDark: colors.blue[400],
    accentLight: colors.blue[300],
  },
  AIRSHIP_RED: {
    primaryDark: colors.red[900],
    primaryLight: colors.red[800],
    secondaryDark: colors.red[600],
    secondaryLight: colors.red[500],
    accentDark: colors.red[400],
    accentLight: colors.red[300],
  },
  AIRSHIP_GOLD: {
    primaryDark: colors.gold[900],
    primaryLight: colors.gold[800],
    secondaryDark: colors.gold[600],
    secondaryLight: colors.gold[500],
    accentDark: colors.gold[400],
    accentLight: colors.gold[300],
  },
};

const DefaultRebateIcon: React.FC<DefaultRebateIconProps> = ({
  theme = 'AIRSHIP_BLUE',
  ...props
}) => {
  const c = COLORS_BY_THEME[theme];
  return (
    <svg
      width="80"
      height="80"
      viewBox="0 0 240 201"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      {...props}
    >
      <g clipPath="url(#clip0_2921_17574)">
        <mask id="path-1-inside-1_2921_17574" fill="white">
          <path d="M0 0L240 0V201H0L0 0Z" />
        </mask>
        <path d="M0 0L240 0V201H0L0 0Z" fill="#F9FAFB" />
        <g clipPath="url(#clip1_2921_17574)">
          <mask id="path-3-inside-2_2921_17574" fill="white">
            <path d="M-41.5 0.5L281.5 0.5V200.5H-41.5V0.5Z" />
          </mask>
          <path
            d="M-41.5 0.5L281.5 0.5V200.5H-41.5V0.5Z"
            fill={c.secondaryLight}
          />
          <rect
            x="-77.5"
            y="123.426"
            width="550.293"
            height="87.2871"
            fill={c.secondaryDark}
          />
          <path
            d="M43.2305 72.423L118.587 117.637V192.993L43.2305 147.779V72.423Z"
            fill={c.secondaryLight}
          />
          <path
            d="M118.59 109.309L149.269 76.1691L188 73.5798V148.936L118.59 192.993V109.309Z"
            fill={c.primaryLight}
          />
          <path
            d="M30.6487 80.4652L110.544 126.397L149.958 80.3333L70.7739 32.6288L30.6487 80.4652Z"
            fill={c.accentDark}
          />
          <path
            d="M30.6487 80.4641L110.544 126.396L110.544 129.493L30.6484 83.586L30.6487 80.4641Z"
            fill={c.accentDark}
          />
          <path
            d="M149.969 80.3352L110.539 126.396L110.539 129.493L149.968 83.4571L149.969 80.3352Z"
            fill={c.accentDark}
          />
          <path
            d="M149.977 80.3366L200.44 74.9888L200.44 78.0854L149.977 83.4586L149.977 80.3366Z"
            fill={c.accentDark}
          />
          <path
            d="M120.406 27.838L200.442 74.9866L150.004 80.3839L70.7695 32.6685L120.406 27.838Z"
            fill={c.accentLight}
          />
          <path
            d="M187.986 148.936V123.428L260.501 123.428L218.167 164.672L118.574 192.993L187.986 148.936Z"
            fill={c.primaryDark}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M137.387 50.5547L121.1 40.9606L108.691 42.1682L124.914 51.9375L124.901 51.9389L144.709 63.8678L157.59 62.464L137.394 50.5539L137.387 50.5547Z"
            fill={c.primaryLight}
          />
          <path
            d="M121.1 40.9604V49.6338L108.691 42.1681L121.1 40.9604Z"
            fill={c.primaryLight}
          />
          <path
            d="M114.41 52.5552C113.101 30.1483 121.235 17.4686 134.718 8.97971L161.905 25.4831C149.991 33.2935 143.245 46.5361 144.056 70.4251L114.41 52.5552Z"
            fill="#F2D497"
          />
          <path
            d="M117.437 53.5457C115.91 35.884 122.501 21.1729 134.714 13.9041L155.075 26.2642C150.412 29.7723 146.718 35.4953 144.218 42.525C141.672 49.6807 140.343 58.2479 140.501 67.3644L117.437 53.5457Z"
            stroke="#BB9446"
            strokeWidth="1.5321"
          />
          <path
            d="M134.349 33.0319C139.5 35.111 142.011 40.4623 140.23 44.8776C138.448 49.293 132.926 51.4036 127.774 49.3246C122.622 47.2455 120.111 41.8934 121.893 37.478C123.675 33.0628 129.197 30.9528 134.349 33.0319Z"
            stroke="#BB9446"
            strokeWidth="1.83708"
          />
          <path
            d="M123.331 38.8411L123.915 37.5214L125.525 38.2339C125.771 37.7653 126.085 37.4039 126.479 37.0781C126.92 36.7344 127.35 36.5014 127.8 36.3542L128.682 38.0149C127.608 38.3095 127.2 39.0141 127.065 39.3199C126.915 39.6579 126.771 40.1138 126.967 40.5852C127.092 40.9103 127.33 41.1119 127.588 41.2259C127.91 41.3684 128.192 41.3394 128.529 41.1423C128.76 41.0133 129.002 40.8128 129.773 40.1148C130.348 39.5993 130.956 38.964 131.73 38.652C132.533 38.3148 133.316 38.3727 134.089 38.7147C135.119 39.1706 135.779 39.9828 136.059 40.7418C136.328 41.4383 136.358 42.2407 136.194 42.9575L137.997 43.7554L137.413 45.0751L135.642 44.2914C135.147 45.1498 134.503 45.6922 133.997 46.0075C133.569 46.2797 133.013 46.5343 132.302 46.6622L131.598 44.9066C131.996 44.8328 132.571 44.6639 132.969 44.4168C133.46 44.1337 133.812 43.7311 134.068 43.1518C134.253 42.7333 134.373 42.2471 134.191 41.7434C134.039 41.3487 133.726 41.0561 133.372 40.8994C132.969 40.7213 132.594 40.7862 132.323 40.8777C132.051 40.9692 131.378 41.576 131.182 41.7585C130.775 42.1558 129.869 42.9863 128.995 43.3503C128.285 43.6516 127.663 43.665 126.81 43.2874C126.102 42.9739 125.518 42.4655 125.167 41.694C124.946 41.1922 124.754 40.4526 124.94 39.5535L123.331 38.8411Z"
            fill="#BB9446"
          />
          <rect
            x="105.34"
            y="39.9637"
            width="54.491"
            height="9.57728"
            transform="rotate(31.258 105.34 39.9637)"
            fill={c.accentLight}
          />
        </g>
        <path
          d="M281.5 199.5H-41.5V201.5H281.5V199.5Z"
          fill="#636B74"
          fillOpacity="0.3"
          mask="url(#path-3-inside-2_2921_17574)"
        />
      </g>
      <path
        d="M239 0V201H241V0L239 0Z"
        fill="#636B74"
        fillOpacity="0.3"
        mask="url(#path-1-inside-1_2921_17574)"
      />
      <defs>
        <clipPath id="clip0_2921_17574">
          <path d="M0 0L240 0V201H0L0 0Z" fill="white" />
        </clipPath>
        <clipPath id="clip1_2921_17574">
          <path d="M-41.5 0.5L281.5 0.5V200.5H-41.5V0.5Z" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export default DefaultRebateIcon;
